import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { RetailersService, BrandsService, FilterRequestForCombo } from '@src/app/shared/backend/clients';
import { BaseListComponent } from '@src/app/shared/components/base-list/base-list.component';
import { CountryService } from '@src/app/shared/services/countries-service';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { TableSettingService } from '@src/app/shared/services/table.setting.service';
import { ExportService } from '@src/app/shared/services/export.service';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToasterService } from '@src/app/shared/components/toaster/toaster.service';
import { FiltersPanelChangeEvent, FiltersPanelFieldOverrides, FiltersPanelDropdownLazyLoadEvent } from '@src/app/shared/components/filters-panel/filters-panel.models';
import { applyFiltersPanelChange } from '@src/app/shared/components/filters-panel/filters-panel.helper';
import { ActiveInactivePipe } from '@app/shared/pipes/activeInactive.pipe';
import { loadStoredPaginatorState, resetPaginatorStateToFirstPage } from '@app/shared/components/paginator/paginator-storage.helper';
import { buildRemoteDropdownRequest, normalizeDropdownState } from '@src/app/shared/components/filters-panel/filters-panel-dropdown.helpers';

@Component({
    selector: 'app-retailers',
    templateUrl: './retailers.component.html',
    styleUrl: './retailers.component.scss',
    host: {
        class: 'app-retailers'
    },
    standalone: false
})
export class RetailersComponent extends BaseListComponent implements OnInit, OnDestroy {

  private initialLoadDone = false;
  private readonly componentDestroyed$ = new Subject<void>();

  public readonly retailersTableName = TableSettingService.RetailsTableName;
  public filtersPanelFieldOverrides: FiltersPanelFieldOverrides = {};

  // Dropdown: countries
  private countryOptions: Array<{ id: string | number; text: string }> = [];

  // Dropdown: status
  private statusOptions: Array<{ id: string | number; text: string }> = [];

  // Dropdown: brands
  brands = {
    results: [],
    pageIndex: 1,
    totalPages: 1,
    totalCount: 0,
    hasPreviousPage: false,
    hasNextPage: false
  };

  allowCreateRetailer: boolean = false;
  exports: boolean = false;
  currentDate: Date = new Date();

  displayExportModalbox: boolean = false;

  constructor(
    private router: Router,
    private translateService: TranslateService,
    private toasterService: ToasterService,
    public permissionService: PermissionService,
    private countryService: CountryService,
    public override tableSettingService: TableSettingService,
    private exportService: ExportService,
    private retailersService: RetailersService,
    private brandService: BrandsService
  ) {
    super();

    const pipe = new ActiveInactivePipe(this.translateService);
    this.statusOptions = pipe.values;
  }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    this.restorePaginatorStateFromStorage();

    // i18n propagation on lang change
    this.rebuildStatusOptions();

    this.translateService.onLangChange
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => {
        this.rebuildStatusOptions();
        this.rebuildFiltersPanelOverrides();
        this.rebuildSortPanelColumnLabels();
      });

    await this.tableSettingService.init();
    await super.setCache(this.retailersTableName);

    this.initSortPanel({
      defaultSort: { key: 'code', direction: 'ASC' },
      labelResolver: (col) => {
        const translationKey = `TABLE.HEADER.${col.header}`;
        const translated = this.translateService.instant(translationKey);

        if (translated && translated !== translationKey) {
          return translated;
        }

        return col.header;
      }
    });

    // build options used by panel
    this.countryOptions = await this.countryService.getCountries();

    const statusPipe = new ActiveInactivePipe(this.translateService);
    this.statusOptions = statusPipe.values as any;

    this.rebuildSortPanelColumnLabels();
    this.rebuildFiltersPanelOverrides();

    // Fallback: if the panel doesn't restore anything, load unfiltered!!
    setTimeout(async () => {
      if (!this.initialLoadDone) {
        this.initialLoadDone = true;
        await this.get();
      }
    });

    this.exports = await this.permissionService.hasActionPermissions(Actions.export_retailers);
    this.allowCreateRetailer = await this.permissionService.hasActionPermissions(Actions.create_retailers);
  }

  private rebuildStatusOptions() {
    const pipe = new ActiveInactivePipe(this.translateService);
    this.statusOptions = pipe.values;
  }

  private rebuildFiltersPanelOverrides() {
    this.filtersPanelFieldOverrides = {
      isoCountry: {
        fieldLabel: 'TABLE.HEADER.Country',
        isMultiselect: true,

        options: this.countryOptions ?? [],
        optionLabel: 'translationKey', // i18n support
        optionValue: 'id', // id -> alpha2
        translateOptionLabel: true,

        searchMode: 'local',
        showSearchbox: true,
        showClear: true,
        compactUi: true,

        pageSize: 300
      },

      isActive: {
        fieldLabel: 'TABLE.HEADER.Status',
        isMultiselect: true,
        options: this.statusOptions ?? [],
        optionLabel: 'text',
        optionValue: 'id',

        placeholder: 'Select...',
        searchMode: 'local',
        showSearchbox: false,
        showClear: true,
        compactUi: true
      },
      'brands.id': {
        fieldLabel: 'TABLE.HEADER.Brands',
        isMultiselect: true,

        options: this.brands.results ?? [],
        optionLabel: 'text',
        optionValue: 'id',

        placeholder: 'Select...',
        searchPlaceholder: 'Search...',
        searchMode: 'remote',

        showSearchbox: true,
        showClear: true,
        compactUi: true,

        pageSize: 10,
        totalCount: this.brands.totalCount ?? 0,
        totalPages: this.brands.totalPages ?? 1,
        pageIndex: this.brands.pageIndex ?? 1,
        hasPreviousPage: this.brands.hasPreviousPage ?? false,
        hasNextPage: this.brands.hasNextPage ?? false
      }
    };
  }

  private rebuildSortPanelColumnLabels(): void {
    const visibleColumns = Array.isArray(this.tableColumns) ? this.tableColumns : [];

    const nextSortableColumns = visibleColumns
      .filter((col: any) => {
        const field = String(col?.field ?? '').trim();
        return (
          field.length > 0 &&
          field.toLowerCase() !== 'actions' &&
          col?.sortable !== false
        );
      })
      .map((col: any) => {
        const translationKey = `TABLE.HEADER.${col.header}`;
        const translated = this.translateService.instant(translationKey);

        return {
          key: col.field,
          label: translated && translated !== translationKey ? translated : col.header
        };
      });

    this.sortableColumns = nextSortableColumns;

    // clear active sort if points to col that is not available due to system settings update
    const allowedKeys = new Set(nextSortableColumns.map(col => col.key));
    if (this.sortPanelSort?.key && !allowedKeys.has(this.sortPanelSort.key)) {
      this.onSortPanelChange(null);
    }
  }

  protected override onCriteriaResetRequested(): void {
    resetPaginatorStateToFirstPage({
      tableName: this.retailersTableName,
      pageSize: this.pageSize,
    });
  }

  private restorePaginatorStateFromStorage(): void {
    const stored = loadStoredPaginatorState(this.retailersTableName);

    if (!stored) {
      return;
    }

    const storedPageNumber = stored.valuesByFieldKey.pageNumber;
    const storedPageSize = stored.valuesByFieldKey.pageSize;

    this.pageNumber = storedPageNumber;
    this.pageSize = storedPageSize;

    this.filter.pageNumber = storedPageNumber;
    this.filter.pageSize = storedPageSize;
  }

  override async get() {
    this.filter.pageNumber = this.pageNumber;
    this.filter.pageSize = this.pageSize;

    this.applySortToFilterRequest();

    this.page = await firstValueFrom(this.retailersService.apiRetailersFilterTablePost(this.filter));
    this.data = this.page.results;
  }

  override onPageChange(event: any): void {
    this.initialLoadDone = true;
    super.onPageChange(event);
  }

  async onFiltersPanelChange(evt: FiltersPanelChangeEvent): Promise<void> {
    const currentFilters = this.filter.filters as any;

    const nextFilters = applyFiltersPanelChange(
      evt,
      currentFilters,
      this.allTableColumns
    );

    if (!nextFilters) {
      return;
    }

    this.filter.filters = nextFilters as any;

    const isApplyAction = evt.source === 'apply';
    const isClearAction = evt.source === 'clear';
    const shouldResetPaging = isApplyAction || isClearAction;

    if (shouldResetPaging) {
      const firstPageNumber = 1;

      this.pageNumber = firstPageNumber;
      this.filter.pageNumber = firstPageNumber;

      this.onCriteriaResetRequested();
    }

    this.initialLoadDone = true;
    await this.get();
  }

  goToRetailerDetails(event: any, retailerId: string) {
    this.router.navigate([`/retailers/${retailerId}`]);
  }

  goToCreateRetailerForm() {
    this.router.navigate(['/retailers/add']);
  }

  onOpenExportModalbox() {
    this.displayExportModalbox = true;
  }

  handleExportSubmit(form: FormGroup) {
    const range = form.value.dateRange;
    const fileName = form.value.exportName;
    const fileType = form.value.format;

    this.exportService.exportData(
      this.filter,
      [range?.[0], range?.[1] ?? new Date()],
      this.retailersService.apiRetailersExportPost.bind(this.retailersService),
      {
        fileName,
        fileType,
        onSuccess: () => {
          const title = this.translateService.instant('EXPORT_SETTINGS.TOAST_SUCCESS_TITLE');
          const message = this.translateService.instant('EXPORT_SETTINGS.TOAST_SUCCESS_MESSAGE');
          this.toasterService.show('success', title, message);
        },
        onError: (error) => {
          const title = this.translateService.instant('EXPORT_SETTINGS.TOAST_ERROR_TITLE');
          const message = this.translateService.instant('EXPORT_SETTINGS.TOAST_ERROR_MESSAGE');
          this.toasterService.show('error', title, message);
          console.error('Export error:', error);
        }
      }
    );
  }

  ngOnDestroy() {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }

  async onFiltersPanelDropdownLazyLoad(evt: FiltersPanelDropdownLazyLoadEvent): Promise<void> {
    if (!evt) {
      return;
    }

    // fetch dropdowns remote data
    const handlers: Record<string, (e: FiltersPanelDropdownLazyLoadEvent) => Promise<void>> = {
      'brands.id': (e) => this.loadBrandsCombo(e)
    };

    const handler = handlers[evt.fieldKey];
    if (!handler) {
      return;
    }

    await handler(evt);
  }

  private async loadBrandsCombo(evt: FiltersPanelDropdownLazyLoadEvent): Promise<void> {
    const requestPayload: FilterRequestForCombo = buildRemoteDropdownRequest({
      pageIndex: evt.pageIndex,
      pageSize: evt.pageSize,
      search: evt.search
    });

    const response = await firstValueFrom(
      this.brandService.apiBrandsFilterComboPost(requestPayload)
    );

    this.brands = normalizeDropdownState(response as any, evt.pageIndex) as any;
    this.rebuildFiltersPanelOverrides();
  }
}
